/**
 * CategoriesLanding — /categories
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Hash, Newspaper, Users, Plane, Utensils, Sprout, Briefcase,
  GraduationCap, Heart, Leaf, FlaskConical, Cpu, Trophy, Clapperboard,
  Music, Home as HomeIcon, Calendar, Bus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/categories`;

const ICON_MAP = {
  "newspaper": Newspaper, "users": Users, "plane": Plane, "utensils": Utensils,
  "sprout": Sprout, "briefcase": Briefcase, "graduation-cap": GraduationCap,
  "heart": Heart, "leaf": Leaf, "flask-conical": FlaskConical, "cpu": Cpu,
  "trophy": Trophy, "clapperboard": Clapperboard, "music": Music,
  "home": HomeIcon, "calendar": Calendar, "bus": Bus,
};

const ACCENT = {
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  orange:  "bg-orange-50 text-orange-700 border-orange-200",
  lime:    "bg-lime-50 text-lime-700 border-lime-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  cyan:    "bg-cyan-50 text-cyan-700 border-cyan-200",
  yellow:  "bg-yellow-50 text-yellow-700 border-yellow-200",
  purple:  "bg-purple-50 text-purple-700 border-purple-200",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  blue:    "bg-blue-50 text-blue-700 border-blue-200",
  red:     "bg-red-50 text-red-700 border-red-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
};

export default function CategoriesLanding() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    fetch(API).then(r => r.json()).then((d) => setCats(d.categories || []));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="categories-landing">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
        <Hash className="w-3 h-3" /> All Categories
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Explore every topic</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">
        Each topic is a lens on the same global conversation. Pick one, add a location, and see what the community is saying.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8" data-testid="categories-grid">
        {cats.map((c) => {
          const Icon = ICON_MAP[c.icon] || Hash;
          const accent = ACCENT[c.accent] || ACCENT.slate;
          return (
            <Link
              key={c.slug}
              to={`/c/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className={`rounded-2xl p-4 border transition hover:shadow-lg ${accent}`}
            >
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-white/70 inline-flex items-center justify-center border border-white shadow-sm shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900">{c.label}</h3>
                    <span className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{c.discussions_count} threads</span>
                  </div>
                  <p className="text-xs mt-1 opacity-80 line-clamp-2">{c.description}</p>
                  {c.example_topics && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.example_topics.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-white/80 text-slate-700">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
